import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { PpApiGatewayService } from '@app/pp-api-gateway.service';
import { FINANCED_CHARGE_TYPES, RatePlan } from '@app/types/rate-plan';
import { SessionStorage } from '@app/types/session-storage.enum';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  AUTO_RENEW_UPDATE_COUNTRIES,
  CANCELLATION_IN_PROGRESS_KEY,
  Countries,
  DIALOG_CONFIG,
  DataType,
  FLOWS_DIALOGS,
  FLOWS_TRANSLATIONS,
  Flows
} from './auto-renew.type';
import { UserService } from '@app/user.service';

@Injectable()
export class AutoRenewService {
  public autoRenewUpdated$ = new BehaviorSubject(null);
  private ratePlan: RatePlan;
  private accountId: string;
  private isFinanced: boolean;

  public get isAutoRenewDisabled() {
    return this.ratePlan.autoRenew && !this.ratePlan.renewalDate;
  }

  constructor(
    private dialog: MatDialog,
    private ppApiGatewayService: PpApiGatewayService,
    private userService: UserService
  ) { }

  public initialize(ratePlan: RatePlan, accountId: string) {
    this.ratePlan = ratePlan;
    this.accountId = accountId;
    this.isFinanced = FINANCED_CHARGE_TYPES.includes(this.ratePlan.charge);
  }

  public trigger(
    autoRenew: boolean,
    onSuccess: (flow: Flows, value: any) => void,
    onError: (flow: Flows, value: any) => void,
  ) {
    const countryISO = sessionStorage.getItem(SessionStorage.Country);
    const isCustomCountry = AUTO_RENEW_UPDATE_COUNTRIES.includes(countryISO as Countries);
    const servicePhoneNumber = decodeURI(sessionStorage.getItem(SessionStorage.ServicePhoneNumber));
    const studentPhoneNumber = sessionStorage.getItem(SessionStorage.StudentPhoneNumber);

    let data: DataType = {
      servicePhoneNumber,
      studentPhoneNumber,
      confirm$: this.requestCancellationCall$()
    };

    let selectedFlow: Flows = Flows.CANCELLATION_REQUEST;

    if (isCustomCountry && !this.isFinanced) {
      selectedFlow = Flows.AUTO_RENEW_UPDATE;
      data = {
        confirm$: this.updateAutoRenewal$(autoRenew, this.accountId),
        autoRenew,
      };
    }

    if (this.getCancellationInProgress()) {
      selectedFlow = Flows.CANCELLATION_IN_PROGRESS;
      data = {
        servicePhoneNumber,
      };
    }

    const callbacks = {
      onSuccess: (value: any) => onSuccess(selectedFlow, value),
      onError: (value: any) => onError(selectedFlow, value),
    };

    this.openDialog(selectedFlow, { ...data, ...callbacks });
  }

  private openDialog(flow: Flows, data: any) {
    this.dialog.open(
      FLOWS_DIALOGS[flow],
      this.getDialogConfig(flow, data)
    );
  }

  private getDialogConfig(flow: Flows, data: any) {
    return ({
      data: {
        ...FLOWS_TRANSLATIONS[flow],
        ...data
      },
      ...DIALOG_CONFIG
    });
  }

  private setCancellationInProgress() {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 3);
    window.localStorage.setItem(CANCELLATION_IN_PROGRESS_KEY, expirationDate.toUTCString());
  }

  private getCancellationInProgress(): boolean {
    const expirationDateEntry = window.localStorage.getItem(CANCELLATION_IN_PROGRESS_KEY);
    if (!expirationDateEntry) { return false; }
    const expirationDate = new Date(expirationDateEntry);
    return expirationDate.getTime() > Date.now();
  }

  private requestCancellationCall$() {
    return this.ppApiGatewayService.requestCancellationCall(this.userService.contactId).pipe(
      tap(() => this.setCancellationInProgress())
    );
  }

  private updateAutoRenewal$(autoRenew, accountId) {
    return this.ppApiGatewayService.updateAutoRenewal(autoRenew, accountId).pipe(
      tap(() => this.autoRenewUpdated$.next(null))
    );
  }

}
